import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile CAPTCHA widget. Invisible managed mode.
 * Calls onVerify(token) when the user is validated.
 *
 * Usage:
 *   <TurnstileWidget onVerify={(token) => setCaptchaToken(token)} />
 */
export default function TurnstileWidget({ onVerify, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Load the Turnstile script once
  useEffect(() => {
    if (document.getElementById("cf-turnstile-script")) {
      setLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Render the widget when script is loaded and we have a site key
  useEffect(() => {
    if (!loaded || !siteKey || !containerRef.current || !window.turnstile) return;
    if (widgetIdRef.current) return;

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token) => onVerify && onVerify(token),
      "error-callback": () => onError && onError(),
      "expired-callback": () => onVerify && onVerify(null),
      appearance: "always",
      theme: "dark",
    });

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
  }, [loaded, siteKey, onVerify, onError]);

  if (!siteKey) {
    return null; // CAPTCHA disabled if site key missing
  }

  return <div ref={containerRef} data-testid="turnstile-widget" className="my-2" />;
}
